import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styles from './ActionsPage.module.css';
import { PageShell, type PageShellRender } from '@/components/PageShell';
import { KpiStrip, type Kpi } from '@/components/KpiStrip';
import { Panel, EmptyState } from '@/components/Panel';
import { Tag } from '@/components/MetricTable';
import { DataSourceBanner } from '@/features/availability/DataSourceBanner';
import { fetchActions, type ActionItem, type ActionsFeed } from '@/lib/pages.api';
import { inr, inrExact, plain, shortDate } from '@/lib/money';
import { titleCase } from '@/lib/format';

/**
 * Actions — Overview > Actions.
 *
 * A worklist, ordered by what it is worth. Each row names the rule that fired,
 * the feed it came from and the team that owns it, and expands to the numbers
 * behind it. Nothing here is a recommendation the dashboard invented: every
 * flag is one the warehouse already computes, which is what keeps this page
 * and the MCP tool answering the same question the same way.
 *
 * THERE IS NO HEADLINE TOTAL, AND THAT IS THE POINT. Rupees at risk next week,
 * rupees still owed on a purchase order and rupees of capital tied up in
 * overstock are three different questions. One figure across them overstated
 * the exposure and could not be acted on, so the strip carries one tile per
 * KIND of money and the page never adds them together.
 */

const OWNER_TONE = {
  Brand: 'neutral',
  Blinkit: 'warn',
  Shared: 'neutral',
} as const;

export function ActionsPage({
  onNavigate,
  onSignOut,
}: {
  onNavigate?: (section: string, page: string) => void;
  onSignOut?: () => void;
}) {
  const [resolved, setResolved] = useState<ActionsFeed['window'] | undefined>(undefined);
  return (
    <PageShell
      title="Actions"
      activeNavId="actions"
      activeSection="overview"
      chipKinds={['city']}
      {...(onNavigate ? { onNavigate } : {})}
      {...(onSignOut ? { onSignOut } : {})}
      resolved={resolved ? { ...resolved } : undefined}
    >
      {(ctx) => (
        <ActionsBody ctx={ctx} onResolved={setResolved} {...(onNavigate ? { onNavigate } : {})} />
      )}
    </PageShell>
  );
}

function ActionsBody({
  ctx,
  onResolved,
  onNavigate,
}: {
  ctx: PageShellRender;
  onResolved: (w: ActionsFeed['window']) => void;
  onNavigate?: (section: string, page: string) => void;
}) {
  const [rule, setRule] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<string | null>(null);

  const params = useMemo(
    () => ({
      customer_name: ctx.customer,
      window: ctx.range,
      filters: {
        ...(ctx.scope.city ? { city: ctx.scope.city } : {}),
        ...(rule ? { rule_id: rule } : {}),
      },
      top: 60,
    }),
    [ctx.customer, ctx.range, ctx.scope.city, rule],
  );

  const query = useQuery({
    queryKey: ['actions-feed', params],
    queryFn: ({ signal }) => fetchActions(params, signal),
    enabled: ctx.customer !== '',
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const data = query.data;
  const win = data?.window;
  useEffect(() => {
    if (win) onResolved(win);
  }, [win, onResolved]);

  const loading = query.isPending && !data;
  const ruleById = useMemo(
    () => new Map((data?.rules ?? []).map((r) => [r.id, r])),
    [data?.rules],
  );

  const kpis: Kpi[] = useMemo(() => {
    const s = data?.summary;
    const kinds = s?.by_kind ?? [];
    return [
      {
        key: 'total',
        label: 'Open actions',
        value: plain(s?.total_actions ?? null),
        foot:
          s && s.shown_actions < s.total_actions
            ? `showing the top ${plain(s.shown_actions)}`
            : rule
              ? 'filtered to one rule'
              : undefined,
        emphasis: true,
      },
      // One tile per KIND of money. Never one tile across kinds — the label on
      // each says what it is, and no two of them may be added.
      ...kinds.map(
        (k): Kpi => ({
          key: `kind-${k.kind}`,
          label: k.label.replace(/^₹\s*/, ''),
          value: k.summable ? inr(k.total) : plain(k.actions),
          foot: k.summable
            ? `${plain(k.actions)} action${k.actions === 1 ? '' : 's'}`
            : 'not totalled — association',
          help: k.summable ? undefined : data?.response_meta.totals_note,
        }),
      ),
    ];
  }, [data, rule]);

  /** Sources that answered with nothing, so an empty list can explain itself. */
  const silent = (data?.response_meta.sources ?? []).filter((s) => s.rows === 0);

  return (
    <>
      {query.isError && (
        <DataSourceBanner kind="error" detail={query.error.message} />
      )}

      <KpiStrip items={kpis} loading={loading} />

      <Panel
        title="What to fix"
        caption={
          data
            ? `As of ${shortDate(data.window.as_of)}. Ordered by severity within each rule, then by value.`
            : undefined
        }
        actions={
          <div className={styles.filters}>
            <button
              type="button"
              className={`${styles.chip} ${rule === undefined ? styles.chipOn : ''}`}
              onClick={() => { setRule(undefined); }}
            >
              All rules
            </button>
            {(data?.summary.by_rule ?? []).map((r) => (
              <button
                key={r.rule_id}
                type="button"
                className={`${styles.chip} ${rule === r.rule_id ? styles.chipOn : ''}`}
                onClick={() => { setRule(rule === r.rule_id ? undefined : r.rule_id); }}
                title={ruleById.get(r.rule_id)?.rule}
              >
                {r.title}
                <span className={styles.chipCount}>{r.actions}</span>
              </button>
            ))}
          </div>
        }
        footnote={
          data
            ? `${data.response_meta.windows_note} ${data.response_meta.totals_note}`
            : undefined
        }
      >
        {loading ? (
          <ul className={styles.list}>
            {Array.from({ length: 6 }, (_, i) => (
              <li key={i} className={styles.rowSkeleton} />
            ))}
          </ul>
        ) : (data?.actions.length ?? 0) === 0 ? (
          <EmptyState>
            Nothing is flagged for this scope.
            {silent.length > 0 && (
              <>
                {' '}
                {silent.map((s) => s.name).join(', ')}{' '}
                {silent.length === 1 ? 'returned' : 'returned'} no rows — that is a
                fact about the data, not a clean bill of health.
              </>
            )}
          </EmptyState>
        ) : (
          <ul className={styles.list}>
            {(data?.actions ?? []).map((a) => (
              <ActionRow
                key={a.key}
                action={a}
                ruleText={ruleById.get(a.rule_id)?.rule}
                source={ruleById.get(a.rule_id)?.source}
                expanded={open === a.key}
                onToggle={() => { setOpen(open === a.key ? null : a.key); }}
                {...(onNavigate ? { onNavigate } : {})}
              />
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="Where these come from"
        caption="Each feed is read independently, so one being behind does not empty the page."
        dense
      >
        <ul className={styles.sources}>
          {(data?.response_meta.sources ?? []).map((s) => (
            <li key={s.name} className={styles.source}>
              <span className={s.ok ? styles.dotOk : styles.dotBad} />
              <span className={styles.sourceName}>{s.name}</span>
              <span className={styles.sourceRows}>
                {s.ok ? `${plain(s.rows)} rows` : 'unavailable'}
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </>
  );
}

function ActionRow({
  action,
  ruleText,
  source,
  expanded,
  onToggle,
  onNavigate,
}: {
  action: ActionItem;
  ruleText?: string;
  source?: string;
  expanded: boolean;
  onToggle: () => void;
  onNavigate?: (section: string, page: string) => void;
}) {
  const sev = action.severity;
  // Held in a local so the click handler closes over a value TypeScript has
  // already narrowed — the alternative is an assertion inside the callback.
  const link = action.link;
  return (
    <li className={styles.row} data-severity={sev}>
      <button type="button" className={styles.rowHead} onClick={onToggle} aria-expanded={expanded}>
        <span className={`${styles.sev} ${styles[`sev${sev}`]}`} aria-label={`Severity ${sev}`} />
        <span className={styles.rowMain}>
          <span className={styles.rowTitle}>{action.title}</span>
          {action.subtitle ? (
            <span className={styles.rowSub}>{titleCase(action.subtitle) ?? action.subtitle}</span>
          ) : null}
        </span>
        <span className={styles.rowTags}>
          <Tag tone={OWNER_TONE[action.owner]}>{action.owner}</Tag>
          <span className={styles.team}>{action.team}</span>
        </span>
        <span className={styles.rowValue}>
          <span className={styles.value}>{inr(action.value_at_stake)}</span>
          <span className={styles.valueLabel}>{action.value_label}</span>
        </span>
      </button>

      {expanded && (
        <div className={styles.detail}>
          {ruleText ? <p className={styles.rule}>{ruleText}</p> : null}
          <dl className={styles.evidence}>
            {action.evidence.map((e) => (
              <div key={e.label} className={styles.evidenceItem}>
                <dt>{e.label}</dt>
                <dd>
                  {e.value === null
                    ? '—'
                    : typeof e.value === 'number'
                      ? e.unit === '₹'
                        ? inrExact(e.value)
                        : `${plain(e.value)}${e.unit && e.unit !== '₹' ? ` ${e.unit}` : ''}`
                      : e.value}
                </dd>
              </div>
            ))}
          </dl>
          <div className={styles.detailFoot}>
            {source ? <span className={styles.source}>{source}</span> : null}
            {link && onNavigate ? (
              <button
                type="button"
                className={styles.goto}
                onClick={() => { onNavigate(link.section, link.page); }}
              >
                Open the page that answers this →
              </button>
            ) : null}
          </div>
        </div>
      )}
    </li>
  );
}
